/**
 * The person's notifications, in one place outside App.js.
 *
 * It owns the loaded list (newest first, deduplicated), the "unseen" count
 * behind the navigator's dot, the live socket changes and the paging through
 * GET /v2/notifications. Screens read it with useNotifications (a React hook)
 * or getState; everything that changes it goes through its methods.
 *
 * Loading: at start, one page of everything recent plus one page of pending
 * invites (Home must see an invite however much else came in since). A
 * panel tab asks for its own category with loadCategory and pages it with
 * loadMore, so an old leave or invoice is one click away instead of lost
 * past a fixed cap.
 */
import { useEffect, useReducer, useRef } from "react"
import apiNotification from "./notification.api"
import { getNotifications } from "../api/v2/notification"

/** The first page of everything. */
export const FIRST_PAGE_SIZE = 100

/** One page of one category. */
export const CATEGORY_PAGE_SIZE = 50

/** What every load asks for: disabled notifications are gone for good. */
const BASE_QUERY = { isDisabled: false }

/** Loaded at start on their own, so Home always sees a pending invite. */
const INVITE_QUERY = { type: ["invite", "orgInvite"] }

const byNewest = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)

const initialState = () => ({
    /** Newest first. */
    list: [],
    /** The navigator's count: new since the person last opened the panel. */
    unseen: 0,
    loaded: false,
    loading: false,
    error: null,
    /** Per category key: { loading, hasMore, loaded } */
    categories: {},
})

function createNotificationStore() {
    let state = initialState()
    let generation = 0
    let sockets = []
    const listeners = new Set()
    const createdListeners = new Set()
    // Per category key, the last page response (with getNext / hasNext).
    const pages = new Map()

    const getState = () => state

    const set = (patch) => {
        state = { ...state, ...patch }
        listeners.forEach((fn) => fn(state))
    }

    const setCategory = (key, patch) =>
        set({ categories: { ...state.categories, [key]: { ...state.categories[key], ...patch } } })

    /** Adds or refreshes notifications by _id and keeps the list newest first. */
    const merge = (items = []) => {
        const byId = new Map(state.list.map((n) => [n._id, n]))

        items.forEach((n) => {
            if (!n?._id || n.isDisabled === true) return
            byId.set(n._id, { ...byId.get(n._id), ...n })
        })

        return [...byId.values()].sort(byNewest)
    }

    /** No unread left: the server's "last read" moves and the dot goes. */
    const settleUnseen = (list) => {
        if (list.some((n) => n.isRead === false)) return state.unseen

        apiNotification.setNotificationUserLastRead().catch(() => {})

        return 0
    }

    /**
     * Loads the first pages and the unseen count. Safe to call again: it
     * starts over (an accepted invite, a merged account).
     *
     * @param [options.extraUnseen] {Number} added to the count: the system
     *   pins (missing timesheets, unlinked clients) count as unseen too
     */
    const start = async ({ extraUnseen = 0 } = {}) => {
        const mine = ++generation

        pages.clear()
        set({ loading: true, error: null, categories: {} })

        try {
            const [count, recent, invites] = await Promise.all([
                apiNotification.getNotificationUnreadCount().catch(() => ({ count: 0 })),
                getNotifications({ ...BASE_QUERY, limit: FIRST_PAGE_SIZE }),
                getNotifications({ ...BASE_QUERY, ...INVITE_QUERY, limit: CATEGORY_PAGE_SIZE }).catch(() => null),
            ])

            if (mine !== generation) return

            state = { ...state, list: [] }
            set({
                list: merge([...(recent?.value || []), ...(invites?.value || [])]),
                unseen: (count?.count || 0) + extraUnseen,
                loaded: true,
                loading: false,
            })
        } catch (err) {
            if (mine !== generation) return

            set({ loading: false, loaded: true, error: err })
        }
    }

    const reload = () => start()

    /**
     * The first page of one category (a panel tab), once.
     *
     * @param key {String} the category
     * @param query {Object} v2 filters: type[], typePrefix[]
     */
    const loadCategory = async (key, query = {}) => {
        if (pages.has(key) || state.categories[key]?.loading) return

        const mine = generation

        setCategory(key, { loading: true })

        try {
            const res = await getNotifications({ ...BASE_QUERY, ...query, limit: CATEGORY_PAGE_SIZE })

            if (mine !== generation) return

            pages.set(key, res)
            set({ list: merge(res?.value || []) })
            setCategory(key, { loading: false, loaded: true, hasMore: !!res?.hasNext?.() })
        } catch (err) {
            if (mine !== generation) return

            setCategory(key, { loading: false, loaded: true, hasMore: false, error: err })
        }
    }

    /** The next page of one category. */
    const loadMore = async (key) => {
        const page = pages.get(key)

        if (!page?.hasNext?.() || state.categories[key]?.loading) return

        const mine = generation

        setCategory(key, { loading: true })

        try {
            const res = await page.getNext()

            if (mine !== generation) return

            pages.set(key, res)
            set({ list: merge(res?.value || []) })
            setCategory(key, { loading: false, hasMore: !!res?.hasNext?.() })
        } catch (err) {
            if (mine !== generation) return

            setCategory(key, { loading: false, error: err })
        }
    }

    /**
     * Marks one or more as read: at once on screen, then on the server.
     *
     * @param ids {String|Array<String>}
     */
    const markRead = (ids) => {
        const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean)

        if (!list.length) return Promise.resolve()

        const wanted = new Set(list)

        set({ list: state.list.map((n) => (wanted.has(n._id) && !n.isRead ? { ...n, isRead: true } : n)) })

        return apiNotification.markNotificationAsRead(list.length === 1 ? list[0] : list).catch(() => {})
    }

    /** Takes one off the list (an invite accepted or declined). */
    const remove = (id) => set({ list: state.list.filter((n) => n._id !== id) })

    /** Many at once: one update, not one per notification */
    const removeMany = (ids) => {
        const gone = new Set(ids)
        set({ list: state.list.filter((n) => !gone.has(n._id)) })
    }

    /** The panel was opened: nothing is new any more. */
    const markAllSeen = () => {
        set({ unseen: 0 })
        return apiNotification.setNotificationUserLastRead().catch(() => {})
    }

    /**
     * Follows the server's changes. Once per session; connect again after
     * reset only.
     */
    const connect = () => {
        if (sockets.length) return

        sockets = [
            apiNotification.onChanges("create", (changes) => {
                const known = new Set(state.list.map((n) => n._id))
                const added = (changes || [])
                    .map((change) => change.doc)
                    .filter((doc) => doc?._id && !known.has(doc._id))

                if (!added.length) return

                set({ list: merge(added), unseen: state.unseen + added.length })
                createdListeners.forEach((fn) => fn(added))
            }),
            apiNotification.onChanges("update", (changes) => {
                const byId = new Map((changes || []).map((change) => [change._id, change.change || {}]))
                const list = state.list
                    .map((n) => {
                        const change = byId.get(n._id)

                        if (!change) return n

                        return {
                            ...n,
                            ...(change.isRead !== undefined ? { isRead: change.isRead } : {}),
                            ...(change.isDisabled !== undefined ? { isDisabled: change.isDisabled } : {}),
                        }
                    })
                    .filter((n) => n.isDisabled !== true)

                set({ list, unseen: settleUnseen(list) })
            }),
            apiNotification.onChanges("delete", (changes) => {
                const gone = new Set((changes || []).map((change) => change._id))
                const list = state.list.filter((n) => !gone.has(n._id))

                if (list.length === state.list.length) return

                set({ list, unseen: settleUnseen(list) })
            }),
        ]
    }

    /** Signed out: forget everything and stop listening. */
    const reset = () => {
        generation += 1
        sockets.forEach((socket) => socket?.disconnect?.())
        sockets = []
        pages.clear()
        state = initialState()
        listeners.forEach((fn) => fn(state))
    }

    const subscribe = (fn) => {
        listeners.add(fn)
        return () => listeners.delete(fn)
    }

    /** Called with the newly arrived notifications (the App's beep). */
    const onCreate = (fn) => {
        createdListeners.add(fn)
        return () => createdListeners.delete(fn)
    }

    return {
        getState,
        subscribe,
        onCreate,
        start,
        reload,
        loadCategory,
        loadMore,
        markRead,
        remove,
        removeMany,
        markAllSeen,
        connect,
        reset,
    }
}

const notificationStore = createNotificationStore()

export default notificationStore

export { createNotificationStore }

/** Same items in the same order. */
export const shallowEqualArray = (a, b) =>
    a === b || (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, i) => item === b[i]))

/**
 * Reads the notification store and re-renders when what the selector picks
 * changes (by isEqual, Object.is by default).
 *
 * @param [selector] {function(Object): *}
 * @param [isEqual] {function(*, *): boolean}
 * @param [store] {Object} another store (tests)
 * @return {*}
 */
export function useNotifications(selector = (s) => s, isEqual = Object.is, store = notificationStore) {
    const [, rerender] = useReducer((n) => n + 1, 0)
    const selected = selector(store.getState())
    const selectorRef = useRef(selector)
    const selectedRef = useRef(selected)
    const isEqualRef = useRef(isEqual)

    selectorRef.current = selector
    selectedRef.current = selected
    isEqualRef.current = isEqual

    useEffect(() => {
        const check = (state) => {
            const next = selectorRef.current(state)

            if (!isEqualRef.current(selectedRef.current, next)) rerender()
        }

        // It may have moved between the render and this effect.
        check(store.getState())

        return store.subscribe(check)
    }, [store])

    return selected
}

/** A task's thread messages (mentions in its comments), for the task tiles. */
export const selectTaskMessages = (taskId) => (state) =>
    state.list.filter(
        (n) => n.type === "mentionComment" && n.data?.roomName === "ActionItem" && n.data?.room === taskId,
    )

/** Whether anything loaded is still unread. */
export const selectHasUnread = (state) => state.list.some((n) => n.isRead === false)
